import React, { ChangeEvent, FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import List from '@material-ui/core/List';
import MuiButton from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon';
import MapMarkerIcon from 'mdi-react/MapMarkerIcon';
import { useScopedState } from '../../lib/state';
import { companyEditState } from '../../context/companyEditStore';
import { WWWCompanyLocation } from '../../common/documents/WWWCompanyDocument';
import { Header } from '../Header';
import { Color } from '../Theme';
import { EditListCreateButton, EditListItem } from './EditListItem';
import { useEditList } from './useEditList';

const Form = styled.div`
  overflow-y: auto;

  .MuiFormControl-root {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
  }
`;

export const EditLocationItem: FC<{
  location: WWWCompanyLocation;
  onToggleIsHidden: (locationID: number) => void;
  onMove: (locationID: number, direction: number) => void;
  onRemove: (locationID: number) => void;
  onEdit: (locationID: number) => void;
}> = ({ location: { locationID, name, street1, postalCode, city, isHidden, fonectaID }, ...actions }) => (
  <div style={{ opacity: isHidden ? 0.55 : 1 }}>
    <EditListItem
      id={locationID}
      title={name}
      subtitle={[street1, postalCode, city].filter((property) => !!property).join(', ')}
      icon={<MapMarkerIcon />}
      disableEditing={!!fonectaID}
      {...actions}
      onToggleIsHidden={!!fonectaID ? actions.onToggleIsHidden : null}
      onRemove={!fonectaID ? actions.onRemove : null}
      isHidden={isHidden}
    />
  </div>
);

export interface EditLocationViewProps {
  locationID: number;
  parentTitle: string;
  onClose: () => void;
}

export const EditLocationView: FC<EditLocationViewProps> = ({ locationID, parentTitle, onClose }) => {
  const { locations, set } = useScopedState(companyEditState, ['locations']);

  const location = locations.find((item) => item.locationID === locationID);

  const handleChange = useCallback(
    (field: keyof WWWCompanyLocation, event: ChangeEvent<HTMLInputElement>) => {
      const { locations } = companyEditState.getState();

      const updatedLocations = [...locations];
      const index = updatedLocations.findIndex((item) => item.locationID === locationID);

      (updatedLocations[index][field] as string) = event.target.value;

      set({ locations: updatedLocations });
    },
    [locationID, set]
  );

  const handleNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => handleChange('name', event), [handleChange]);
  const handleStreet1Change = useCallback((event: ChangeEvent<HTMLInputElement>) => handleChange('street1', event), [handleChange]);
  const handlePostalCodeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => handleChange('postalCode', event), [handleChange]);
  const handleCityChange = useCallback((event: ChangeEvent<HTMLInputElement>) => handleChange('city', event), [handleChange]);
  const handlePhoneChange = useCallback((event: ChangeEvent<HTMLInputElement>) => handleChange('phone', event), [handleChange]);
  const handleEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => handleChange('email', event), [handleChange]);

  return (
    <>
      <div>
        <MuiButton color="primary" startIcon={<ArrowLeftIcon size={16} color={Color.primary} />} size="small" onClick={onClose}>
          {parentTitle}
        </MuiButton>
      </div>

      <Header>
        <FormattedMessage id="company.location.title" defaultMessage="Toimipiste" />
      </Header>

      <Form>
        <TextField
          label={<FormattedMessage id="company.location.firstName" defaultMessage="Nimi" />}
          variant="outlined"
          fullWidth
          value={location.name}
          onChange={handleNameChange}
        />

        <TextField
          label={<FormattedMessage id="company.location.lastName" defaultMessage="Osoite" />}
          variant="outlined"
          fullWidth
          value={location.street1}
          onChange={handleStreet1Change}
        />

        <TextField
          label={<FormattedMessage id="company.location.title" defaultMessage="Postinumero" />}
          variant="outlined"
          fullWidth
          value={location.postalCode}
          onChange={handlePostalCodeChange}
        />

        <TextField
          label={<FormattedMessage id="company.location.firstName" defaultMessage="Postitoimipaikka" />}
          variant="outlined"
          fullWidth
          value={location.city}
          onChange={handleCityChange}
        />

        <TextField
          label={<FormattedMessage id="company.location.phone" defaultMessage="Puhelinnumero" />}
          variant="outlined"
          fullWidth
          value={location.phone}
          onChange={handlePhoneChange}
        />

        <TextField
          label={<FormattedMessage id="company.location.email" defaultMessage="Sähköpostiosoite" />}
          variant="outlined"
          fullWidth
          value={location.email}
          onChange={handleEmailChange}
        />
      </Form>
    </>
  );
};

export interface EditLocationProps {
  parentTitle: string;
  onOpen: (component: JSX.Element) => void;
  onClose: () => void;
}

export const EditLocation: FC<EditLocationProps> = ({ parentTitle, onOpen, onClose }) => {
  const intl = useIntl();

  const removeConfirmationText = intl.formatMessage({
    id: 'company.location.edit.removeConfirmationText',
    defaultMessage: 'Oletko varma että haluat poistaa toimipisteen?',
  });

  const { locations, set } = useScopedState(companyEditState, ['locations']);

  const setList = useCallback((list: typeof locations) => set({ locations: list }), [set]);

  const { toggleIsHidden, move, remove, create } = useEditList({
    list: locations,
    keyColumn: 'locationID',
    removeConfirmationText,
    emptyItem: {
      fonectaID: null,
      locationID: 0,
      name: '',
      street1: '',
      street2: '',
      postalCode: '',
      city: '',
      country: '',
      phone: '',
      email: '',
      isHidden: false,
      isDeleted: false,
    },
    setList,
  });

  /** Render the actual edit view, will be called by the edit dialog when requested */
  const renderEditComponent = useCallback(
    (locationID: number) => <EditLocationView locationID={locationID} parentTitle={parentTitle} onClose={onClose} />,
    [parentTitle, onClose]
  );

  const handleEdit = useCallback(
    (locationID: number) => {
      onOpen(renderEditComponent(locationID));
    },
    [renderEditComponent, onOpen]
  );

  const handleCreate = useCallback(() => {
    const { locationID } = create();
    onOpen(renderEditComponent(locationID));
  }, [create, renderEditComponent, onOpen]);

  const renderLocation = useCallback(
    (location: WWWCompanyLocation) => (
      <EditLocationItem
        key={location.locationID}
        location={location}
        onToggleIsHidden={toggleIsHidden}
        onMove={move}
        onEdit={handleEdit}
        onRemove={remove}
      />
    ),
    [toggleIsHidden, move, remove, handleEdit]
  );

  return (
    <>
      <List disablePadding>
        {locations.filter(({ isDeleted }) => !isDeleted).map(renderLocation)}
        <EditListCreateButton onClick={handleCreate} />
      </List>
    </>
  );
};
